"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Footer } from "@/components/sections/premium/ManifestoSection";

const projects = [
    {
        slug: "yapmarket",
        name: "Yap.market",
        role: "Built for growth from day one.",
        outcome: "Scaled to 10k+ users",
        image: "/work/yapmarket/yapmarket.png",
        details: ["Optimized referral system", "High retention loops", "10k+ users scaled"],
        aspect: "aspect-[16/9]"
    },
    {
        slug: "pp-editor",
        name: "PP Editor",
        role: "AI-Powered Video Editing.",
        outcome: "10x Faster Workflow",
        image: "/work/ppeditor/ppeditor.png",
        details: ["AI Workflows", "Auto-Captioning", "Lightning Fast Export"],
        aspect: "aspect-[4/5]"
    },
    {
        slug: "astra-finance",
        name: "Astra Finance",
        role: "Security defined by intent.",
        outcome: "$50M+ TVL secured",
        image: "/about1.webp",
        details: ["StarkNet Integration", "Audit-ready codebase", "Institutional-grade UX"],
        aspect: "aspect-[16/9]"
    },
    {
        slug: "echo-ai",
        name: "Echo AI",
        role: "Execution over noise.",
        outcome: "30% efficiency gain",
        image: "/about2.webp",
        details: ["Specialized LLM", "Workflow Automation", "Orchestration Pipeline"],
        aspect: "aspect-[3/4]"
    }
];

const ProjectCard = ({ project, index }: { project: any, index: number }) => {
    const isEven = index % 2 === 0;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });

    const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

    return (
        <Link 
            key={project.slug} 
            href={`/viewwork/${project.slug}`}
            className="block group relative"
            ref={ref}
        >
            <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className={`relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${isEven ? "" : "lg:mt-32"}`}
            >
                {/* MASSIVE BACKGROUND ID */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[35vw] font-display text-white/[0.015] leading-none pointer-events-none select-none text-center w-full z-0">
                    0{index + 1}
                </div>

                {/* IMAGE BLOCK */}
                <div className={`relative z-10 ${isEven ? "lg:col-span-8" : "lg:col-span-7 lg:col-start-6 lg:order-last"}`}>
                    <div className={`${project.aspect} overflow-hidden rounded-2xl bg-white/5 relative border border-white/10 group-hover:border-white/30 transition-colors duration-500`}>
                        <motion.div style={{ y }} className="absolute inset-[-10%] w-[120%] h-[120%]">
                            <Image
                                src={project.image}
                                alt={project.name}
                                fill
                                className="object-cover transition-transform duration-1000 ease-out grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 group-hover:scale-[1.02]"
                            />
                        </motion.div>
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-700" />
                    </div>
                </div>

                {/* TYPOGRAPHY BLOCK */}
                <div className={`relative z-10 space-y-8 ${isEven ? "lg:col-span-4 lg:pl-8" : "lg:col-span-5 lg:col-start-1 lg:pr-8"}`}>
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-full border border-primary flex justify-center items-center text-primary font-mono text-xs overflow-hidden relative">
                            <span className="relative z-10">0{index + 1}</span>
                            <div className="absolute inset-0 bg-primary translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                        </div>
                    </div>
                    <h3 className="font-display text-6xl md:text-8xl uppercase tracking-tighter group-hover:text-primary transition-colors duration-500">
                        {project.name}
                    </h3>
                    <p className="font-sans text-2xl text-white/60 uppercase tracking-tight italic">
                        "{project.role}"
                    </p>
                    
                    <div className="pt-8 border-t border-white/10 mt-8">
                        <span className="font-mono text-xs text-white/30 uppercase tracking-[0.3em] block mb-4">/ Key Outcome</span>
                        <span className="font-display text-4xl uppercase tracking-tighter text-white/90">
                            {project.outcome}
                        </span>
                    </div>

                    <ul className="flex flex-wrap gap-2 pt-8">
                        {project.details?.map((detail: string) => (
                            <li key={detail} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 border border-white/10 px-4 py-2 rounded-full group-hover:border-primary/50 group-hover:text-white transition-colors duration-500">
                                {detail}
                            </li>
                        ))}
                    </ul>
                </div>
            </motion.div>
        </Link>
    );
};

export const WorkPageContent = () => {
    return (
        <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
            {/* COLLINS STYLE HERO */}
            <section className="relative h-screen w-full flex flex-col justify-between p-6 md:p-12 overflow-hidden bg-black">
                <div className="absolute inset-0 z-0">
                    <Image
                        src="/ourwork.jpg"
                        alt="Our Work Background"
                        fill
                        className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale mix-blend-screen scale-105"
                        priority
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
                </div>
                
                <div className="relative z-10 flex justify-between items-start mt-24">
                    <motion.span 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
                    >
                        DOTSLASHLAB / SHOWCASE
                    </motion.span>
                    <motion.span 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest uppercase"
                    >
                        PROTOTYPES <br/> TO SCALE
                    </motion.span>
                </div>

                <div className="relative z-10 w-full flex justify-center items-center h-full">
                    <motion.h1 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-[16vw] md:text-[20vw] leading-[0.75] tracking-tighter uppercase text-center"
                    >
                        OUR <br />
                        <span className="text-transparent italic" style={{ WebkitTextStroke: '2px #f4f4f0' }}>WORK.</span>
                    </motion.h1>
                </div>
            </section>

            <section className="py-32 px-4 relative z-10 bg-[#0a0a0a]">
                <div className="max-w-[1400px] mx-auto space-y-40">
                    {projects.map((project, index) => (
                        <ProjectCard key={project.slug} project={project} index={index} />
                    ))}
                </div>
            </section>

            <Footer />

            {/* Background premium detail */}
            <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
        </main>
    );
};
